// src/services/privateAxiosService.ts
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { convertAPIAuthTokenResponseToNormalShape, isAccessTokenExpired } from '../../utils/auth.ts';
import { appConfig } from '../../utils/consts.ts';
import {
  getLocalStorageValue,
  setLocalStorageValue,
  removeLocalStorageValue,
} from '../../utils/local-storage.ts';
import { useAuthStore } from '../store';
import publicAxiosService from './publicAxiosService.ts';

/** Paths that may be called without a token (obtaining initial token) */
const PATHS_ALLOWED_WITHOUT_TOKEN = ['/auth/token', '/auth/demo-token'];

function isPathAllowedWithoutToken(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0];
  return PATHS_ALLOWED_WITHOUT_TOKEN.some((allowed) => path.endsWith(allowed));
}

class PrivateAxiosService {
  private readonly instance: AxiosInstance;
  private refreshPromise: Promise<string | undefined> | null = null;

  constructor(baseURL: string) {
    this.instance = axios.create({
      baseURL,
      timeout: 15000,
    });

    this.instance.interceptors.request.use(
      (config) => {
        const storedValue = getLocalStorageValue('userAccessToken');
        const token = storedValue?.accessToken;

        if (token && isAccessTokenExpired(token)) {
          this.clearAuthData();

          if (!isPathAllowedWithoutToken(config.url)) {
            return Promise.reject(
              new Error(
                'Private API request blocked: access token expired. Please refresh or re-open the game.'
              )
            );
          }
        } else if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        } else if (!isPathAllowedWithoutToken(config.url)) {
          return Promise.reject(
            new Error(
              'Private API request blocked: no access token. Please refresh or re-open the game.'
            )
          );
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const config = error?.config as ExtendedAxiosRequestConfig;

        if (error.response?.status === 401 && !config?.sent) {
          if (config) {
            config.sent = true;
          }

          try {
            const newToken = await this.getRefreshToken();

            if (newToken && config) {
              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${newToken}`,
              };
              return this.instance(config);
            }
            // Only refreshTokenFn() clears the token on failure; we do not clear here,
            // so one request's 401 cannot remove the token while another's refresh is in progress.
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (error) {
            // Token is already cleared by refreshTokenFn() if refresh failed
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async getRefreshToken(): Promise<string | undefined> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.refreshTokenFn();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async refreshTokenFn() {
    const storedValue = getLocalStorageValue('userAccessToken');
    const token = storedValue?.accessToken;

    try {
      if (!token) {
        throw new Error('No token');
      }

      const response = await publicAxiosService.post<APITokenResponse>(
        `/auth/refresh`,
        undefined,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responseData = response.data;

      if (!responseData?.access_token) {
        this.clearAuthData();
        return;
      }

      const convertedData =
        convertAPIAuthTokenResponseToNormalShape(responseData);
      setLocalStorageValue('userAccessToken', convertedData);

      useAuthStore.getState().checkAuthStatus();

      return responseData.access_token;
    } catch (error) {
      this.clearAuthData();
      throw error;
    }
  }

  clearAuthData() {
    removeLocalStorageValue('userAccessToken');
    useAuthStore.getState().checkAuthStatus();
  }

  async getWalletSignMessage(address: string) {
    const response = await publicAxiosService.post<
      undefined,
      { message: string }
    >(`/auth/b2c-signature`, {
      address,
    });

    if (!response?.message) {
      throw new Error('Could not get signature from response: ' + response);
    }

    return response.message;
  }

  async walletLogin(address: string, signature: string) {
    const response = await publicAxiosService.post<undefined, APITokenResponse>(
      `/auth/b2c-token`,
      {
        address,
        signature,
      }
    );

    if (!response?.access_token) {
      throw new Error('Could not get access token from response: ' + response);
    }

    setLocalStorageValue(
      'userAccessToken',
      convertAPIAuthTokenResponseToNormalShape(response)
    );
  }

  // Axios methods shortcuts
  get<T = $TSFixMe, R = AxiosResponse<T>, D = $TSFixMe>(
    url: string,
    config?: AxiosRequestConfig<D>
  ): Promise<R> {
    return this.instance.get<T, R, D>(url, config);
  }

  post<T = $TSFixMe, R = AxiosResponse<T>, D = $TSFixMe>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R> {
    return this.instance.post<T, R, D>(url, data, config);
  }

  put<T = $TSFixMe, R = AxiosResponse<T>, D = $TSFixMe>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R> {
    return this.instance.put<T, R, D>(url, data, config);
  }

  delete<T = $TSFixMe, R = AxiosResponse<T>, D = $TSFixMe>(
    url: string,
    config?: AxiosRequestConfig<D>
  ): Promise<R> {
    return this.instance.delete<T, R, D>(url, config);
  }
}

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  sent?: boolean;
}

export type APITokenResponse = {
  access_token: string;
  token_type: 'bearer';
  expires_in: number;
};

const privateAxiosService = new PrivateAxiosService(appConfig.restAPI);
export default privateAxiosService;
